import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { useUpdateCustomer } from 'Customer/index'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  Common,
  Customer,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useMemo, useState } from 'react'
import { PerformCreateCustomer } from 'modules/Customers/hooks/useCreateCustomer'

type CustomerSelectionField = {
  value: Customer['id']
  onChange: (newValue: Customer['id']) => void
  disabled: boolean
  validationErrors: ValidationResult[]
}
export type WidgetView = 'customerSelection' | 'customerView'

type UseCustomerWidget = () => {
  customer?: Customer
  showEditButton: boolean
  editButtonDisabled: boolean
  editButtonDisabledReason: string | null
  view: WidgetView
  updateView: (newView: WidgetView) => void
  fieldConfig: {
    customer: CustomerSelectionField
    rollUpBilling: {
      hidden: boolean
      isDisabled: boolean
      validationErrors: ValidationResult[]
      value: boolean
      onChange: (value: boolean) => void
      childAccounts: string[]
      childAccountCount: number
    }
  }
  createCustomer: PerformCreateCustomer
}

export const useCustomerWidget: UseCustomerWidget = () => {
  const cubeContext = useCubeContext()
  const customerId = cubeContext.queries.rawData.data.common.customerId
  const customer = cubeContext.queries.rawData.data.customers[customerId]

  const [view, setView] = useState<'customerSelection' | 'customerView'>(
    customer ? 'customerView' : 'customerSelection'
  )

  const loadFullCustomer = useCallback(
    async (newCustomerId: string) => {
      const fullCustomer =
        (await cubeContext.mutators.external.in.customer({
          customerId: newCustomerId
        })) ?? {}

      cubeContext.mutators.updateData({
        customers: {
          [newCustomerId]: fullCustomer
        }
      })
      return
    },
    [cubeContext.mutators]
  )

  const changeSelectedCustomer = useCallback(
    async (newCustomerId: Common['customerId']) => {
      await loadFullCustomer(newCustomerId).catch(() => {
        console.error(`Failed to load full customer with id ${newCustomerId}`)
      })

      await cubeContext.mutators.updateData({
        common: {
          customerId: newCustomerId
        }
      })

      setView('customerView')
    },
    [cubeContext, loadFullCustomer]
  )

  const showEditButton =
    cubeContext.queries.availableFeatures.common.editCustomer.available
      .visible && view !== 'customerSelection'

  const goCardlessEnabled =
    cubeContext.queries.rawData.data.schedule.goCardlessPayment
  const editButtonDisabled = goCardlessEnabled
  const editButtonDisabledReason = goCardlessEnabled
    ? 'Customer cannot be changed when GoCardless is enabled for a billing schedule'
    : null

  const updateView = useCallback((newView: WidgetView) => {
    return setView(newView)
  }, [])

  const childAccounts = useMemo(() => {
    return customer?.organizations?.some(
      org => org?.owner?.id === customerId && org?.members?.length !== 0
    )
      ? (customer?.organizations?.flatMap(
          org => org?.members?.map(member => member?.name) ?? []
        ) ?? [])
      : (customer?.aliases
          ?.map(alias => alias.label || alias.value)
          .sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)) ?? [])
  }, [customer?.aliases, customer?.organizations, customerId])

  const { updateCustomer } = useUpdateCustomer(customer?.id ?? '')
  const updateCustomerAndCube = useCallback(
    async (
      updatedCustomer: Partial<Dashboardv99990101Api.PutCustomer.UpdateCustomerBody>
    ) => {
      const res = await updateCustomer(updatedCustomer)

      if (res) {
        cubeContext.mutators.updateData({
          customers: {
            [customer?.id ?? '']: res
          }
        })
      }

      return res
    },
    [cubeContext.mutators, customer?.id, updateCustomer]
  )

  const handleCreateCustomer = useCallback<PerformCreateCustomer>(
    async ({ body }) => {
      // a "create" in this case is actually an update from status DRAFT to ACTIVE
      const result = await updateCustomerAndCube({
        ...body,
        status: 'ACTIVE'
      })

      if (!result) {
        throw new Error('Failed to create customer')
      }

      return result
    },
    [updateCustomerAndCube]
  )

  const fieldConfig = useMemo(
    () => ({
      customer: {
        disabled: false,
        value: cubeContext.queries.rawData.data.common.customerId,
        onChange: changeSelectedCustomer,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.common
            .customerId ?? []
      },
      rollUpBilling: {
        hidden:
          !cubeContext.queries.availableFeatures.schedule.rollUpBilling
            .available.visible,
        isDisabled:
          !cubeContext.queries.availableFeatures.schedule.rollUpBilling
            .available.enabled,
        validationErrors: [],
        onChange: (value: boolean) => {
          cubeContext.mutators.updateData({
            schedule: {
              rollUpBilling: value
            }
          })
        },
        value: cubeContext.queries.rawData.data.schedule.rollUpBilling,
        childAccounts,
        childAccountCount: childAccounts.length
      }
    }),
    [
      cubeContext.queries.rawData.data.common.customerId,
      cubeContext.queries.rawData.data.schedule.rollUpBilling,
      cubeContext.queries.validation.activeValidationResults?.common.customerId,
      cubeContext.queries.availableFeatures.schedule.rollUpBilling.available
        .visible,
      cubeContext.queries.availableFeatures.schedule.rollUpBilling.available
        .enabled,
      cubeContext.mutators,
      changeSelectedCustomer,
      childAccounts
    ]
  )

  return {
    customer,
    showEditButton,
    editButtonDisabled,
    editButtonDisabledReason,
    view,
    createCustomer: handleCreateCustomer,
    updateView,
    fieldConfig
  }
}
